/// <amd-module name="Agile/Directory/Redux/DirectoryActions" />
import { ExceptionInfo } from "Agile/Contracts/Common";
import { IDirectoryArtifact, IDirectoryDataProviderData, IMyDirectoryDataProviderData } from "Agile/Directory/DirectoryContracts";
import { ActionsUnion, createAction, trackedAction } from "redux/Utils/ActionHelper";
import { DirectoryTelemetryConstants } from "DirectoryConstants";

export const enum DirectoryActionTypes {
    InitialDataLoaded = "Directory/InitialDataLoaded",
    InitialDataLoadFailed = "Directory/InitialDataLoadFailed",
    BeginLoadAllData = "Directory/BeginLoadAllData",
    LoadAllDataSucceeded = "Directory/LoadAllDataSucceeded",
    LoadAllDataFailed = "Directory/LoadAllDataFailed",
    ArtifactUpdated = "Directory/ArtifactUpdated",
    RowGroupCollapsedInitialized = "Directory/RowGroupCollapsedInitialized",
    RowGroupToggled = "Directory/RowGroupToggled",
    ToggleFilter = "Directory/ToggleFilter"
}

export const DirectoryActions = {
    /**
     * Action to populate the initial directory data from the data providers
     * @param directoryData The initial data
     */
    initialDataLoaded: <T extends IDirectoryArtifact>(directoryData: IMyDirectoryDataProviderData<T>) =>
        trackedAction(createAction(DirectoryActionTypes.InitialDataLoaded, directoryData), {
            [DirectoryTelemetryConstants.TEAMS_COUNT]: directoryData.artifacts.length,
            [DirectoryTelemetryConstants.FAVORITE_COUNT]: directoryData.favorites ? directoryData.favorites.length : 0
        }),

    /**
     * Action to indicate an exception was found in the data provider
     * @param exceptionInfo The exception returned from the data provider
     */
    initialDataLoadFailed: (exceptionInfo: ExceptionInfo) => createAction(DirectoryActionTypes.InitialDataLoadFailed, exceptionInfo),

    /** Action to indicate we are starting to load the all group */
    beginLoadAllData: () => createAction(DirectoryActionTypes.BeginLoadAllData),

    /**
     * Action to populate the all group
     * @param allDirectoryData The all group data returned from the data provider
     */
    loadAllDataSucceeded: <T extends IDirectoryArtifact>(allDirectoryData: IDirectoryDataProviderData<T>) =>
        trackedAction(createAction(DirectoryActionTypes.LoadAllDataSucceeded, allDirectoryData), {
            [DirectoryTelemetryConstants.TEAMS_COUNT]: allDirectoryData.artifacts.length
        }),

    /**
     * Action to indicate loading of the all group has failed
     * @param exceptionInfo Reason why the all group failed
     */
    loadAllDataFailed: (exceptionInfo: ExceptionInfo) => createAction(DirectoryActionTypes.LoadAllDataFailed, exceptionInfo),

    /**
     * Action to indicate that a directory artifact has been updated.
     * @param artifactId The id of the artifact
     * @param artifact The updated artifact
     */
    artifactUpdated: <T extends IDirectoryArtifact>(artifactId: string, artifact: T) =>
        createAction(DirectoryActionTypes.ArtifactUpdated, {
            artifactId,
            artifact
        }),

    /** Action which populates the row group collapsed state */
    rowGroupCollapsedInitialized: (mapping: { [key: string]: boolean }) => createAction(DirectoryActionTypes.RowGroupCollapsedInitialized, mapping),

    /**
     * Action which expands/collapses a row group
     * @param rowGroupKey The row group key to expand/collapse
     */
    rowGroupToggled: (rowGroupKey: string, collapsed: boolean) =>
        createAction(DirectoryActionTypes.RowGroupToggled, {
            rowGroupKey,
            collapsed
        }),

    /** Action which toggles the visibility of the filter */
    toggleFilter: () => createAction(DirectoryActionTypes.ToggleFilter)
};

export type DirectoryActions = ActionsUnion<typeof DirectoryActions>;
